import React, { useState } from "react";
import { AlertTriangle, ShieldCheck, X, RotateCcw } from "lucide-react";
import { adminApi } from "./adminApi";

const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,27,45,0.55)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16,
};
const card = {
  background: "white", borderRadius: 14, width: "100%", maxWidth: 460,
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden",
  fontFamily: "-apple-system,Segoe UI,Roboto,sans-serif",
};

// mode: "delete" | "restore"
export default function CustomerLifecycleModal({ customer, mode = "delete", onClose, onDone }) {
  const isDelete = mode === "delete";
  const [step, setStep] = useState("intro"); // intro | otp | working | done
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  const name = customer.company_name || customer.name || customer.id;

  const requestOtp = async () => {
    setBusy(true); setErr("");
    const res = isDelete ? await adminApi.deleteRequest(customer.id)
                         : await adminApi.restoreRequest(customer.id);
    setBusy(false);
    if (!res.ok) { setErr(humanize(res.error)); return; }
    setSentTo(res.data?.sent_to || "founder");
    setStep("otp");
  };

  const confirm = async () => {
    if (!/^\d{4,8}$/.test(otp.trim())) { setErr("Enter the 6-digit code from the email."); return; }
    setBusy(true); setErr("");
    const res = isDelete ? await adminApi.deleteConfirm(customer.id, otp.trim())
                         : await adminApi.restoreConfirm(customer.id, otp.trim());
    setBusy(false);
    if (!res.ok) { setErr(humanize(res.error)); return; }
    setStep("done");
    setTimeout(() => { onDone && onDone(); }, 900);
  };

  const accent = isDelete ? "#B91C1C" : "#047857";

  return (
    <div style={overlay} data-testid="lifecycle-modal" onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #E5E7EB" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {isDelete ? <AlertTriangle size={16} color={accent} /> : <RotateCcw size={16} color={accent} />}
            <span style={{ fontWeight: 800, fontSize: 15, color: "#0F1B2D" }}>
              {isDelete ? "Delete customer" : "Restore customer"}
            </span>
          </div>
          <button data-testid="lifecycle-close" onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
            <b>{name}</b> {customer.email ? <span style={{ color: "#6B7280" }}>· {customer.email}</span> : null}
          </div>

          {step === "intro" && (
            <div>
              <p style={{ fontSize: 12.5, color: "#4B5563", lineHeight: 1.6 }}>
                {isDelete ? (
                  <>This is a <b>reversible soft-delete</b>. The customer is hidden from all lists and every sync/scheduled job stops. No financial data is purged — you can restore them later. This requires a one-time code emailed to the founder.</>
                ) : (
                  <>Restoring clears the deletion flag, re-enables syncs, and makes the customer visible again. This requires a one-time code emailed to the founder.</>
                )}
              </p>
              {err && <div data-testid="lifecycle-error" style={errStyle}>{err}</div>}
              <button data-testid="lifecycle-request-otp" disabled={busy} onClick={requestOtp}
                style={{ ...btn, background: accent, marginTop: 14, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Sending…" : "Email OTP to founder"}
              </button>
            </div>
          )}

          {step === "otp" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#047857", marginBottom: 10 }}>
                <ShieldCheck size={14} /> Code sent to <b>{sentTo}</b> · expires in 10 minutes
              </div>
              <input data-testid="lifecycle-otp-input" autoFocus value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit code"
                style={{ width: "100%", height: 42, textAlign: "center", letterSpacing: 6, fontSize: 20, fontWeight: 800, border: "1px solid #D1D5DB", borderRadius: 8, color: "#0F1B2D" }} />
              {err && <div data-testid="lifecycle-error" style={errStyle}>{err}</div>}
              <button data-testid="lifecycle-confirm" disabled={busy} onClick={confirm}
                style={{ ...btn, background: accent, marginTop: 14, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Verifying…" : (isDelete ? "Confirm delete" : "Confirm restore")}
              </button>
              <button data-testid="lifecycle-resend" disabled={busy} onClick={requestOtp}
                style={{ ...btn, background: "white", color: "#6B7280", border: "1px solid #D1D5DB", marginTop: 8 }}>
                Resend code
              </button>
            </div>
          )}

          {step === "done" && (
            <div data-testid="lifecycle-done" style={{ textAlign: "center", padding: "12px 0" }}>
              <ShieldCheck size={28} color={accent} />
              <div style={{ fontWeight: 700, color: "#0F1B2D", marginTop: 8 }}>
                {isDelete ? "Customer deleted" : "Customer restored"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const btn = { width: "100%", color: "white", border: "none", borderRadius: 8, padding: "11px 0", fontSize: 13, fontWeight: 700, cursor: "pointer" };
const errStyle = { marginTop: 10, background: "#FEE2E2", color: "#B91C1C", fontSize: 12, padding: "8px 10px", borderRadius: 6 };

function humanize(detail) {
  const map = {
    sync_in_progress_retry_shortly: "A Tally sync just landed — wait a few seconds and try again.",
    too_many_otp_requests: "Too many OTP requests. Please wait a few minutes.",
    invalid_or_expired_otp: "That code is invalid or expired. Request a new one.",
    already_deleted: "This customer is already deleted.",
    not_deleted: "This customer is not deleted.",
    founder_email_not_configured: "Founder email isn't configured on the server.",
    customer_not_found: "Customer not found.",
  };
  if (typeof detail === "string" && detail.startsWith("softdelete_migration_required"))
    return "Soft-delete migration not applied yet. Run the SQL in Supabase first.";
  return map[detail] || detail || "Something went wrong.";
}
